import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Text,
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Badge,
  MenuListProps,
  MenuButtonProps,
  FlexProps,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SearchBarInput } from 'packages/common-frontend/components/SearchAndCopyComponents';
import { z } from 'zod/v4';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { colors } from '~/packages/common-frontend/colors';
import { useQueryParams } from '~/packages/common-frontend/hooks/useQueryParams';
import { useCreditFacilityContext } from '~/packages/credit-facility/hooks/CreditFacilityProvider';

// Constants
export const creditFacilityIdQueryParam = 'creditFacility';

// Types
const RouteParamsStruct = z.object({
  creditFacilityId: z.string({ message: 'Missing URL parameter' }),
});

// Fragments
export const CommonFacilitySelect = ({
  isDisabled,
  isSearchable = false,
  facilitySlugFilter,
  selectedFacilityId,
  onFacilitySelect,
  placement = 'bottom-end',
  flexProps = { width: ['90vw', '75vw', '50vw', '33vw', '25vw'] },
  hideWhenNotEnoughFacilities = true,
  menuButtonProps = { width: ['90vw', '75vw', '50vw', '33vw', '25vw'] },
  menuListProps = { width: ['90vw', '75vw', '50vw', '33vw', '25vw'] },
  matchWidth = false,
}: {
  isDisabled?: boolean;
  isSearchable?: boolean;
  facilitySlugFilter?: (creditFacilitySlug: string) => boolean;
  selectedFacilityId?: string;
  onFacilitySelect: (selectedCreditFacilityId: string) => void;
  placement?: React.ComponentProps<typeof Menu>['placement'];
  flexProps?: FlexProps;
  hideWhenNotEnoughFacilities?: boolean;
  menuButtonProps?: MenuButtonProps;
  menuListProps?: MenuListProps;
  matchWidth?: boolean;
}) => {
  const menuControls = useDisclosure();

  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilities = facilities.filter(
    (facility) => facilitySlugFilter?.(facility.slug) ?? true,
  );

  // Select facility once facilities load
  const matchedFacility = selectedFacilityId
    ? creditFacilities.find((facility) => facility.id === selectedFacilityId)
    : undefined;

  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredFacilities = useMemo(
    () =>
      isSearchable
        ? creditFacilities.filter((facility) =>
            facility.name.toLowerCase().includes(searchQuery.toLowerCase()),
          )
        : creditFacilities,
    [isSearchable, searchQuery, creditFacilities],
  );

  // Focus the input when menu is opened
  useEffect(() => {
    const timeoutId = menuControls.isOpen
      ? setTimeout(() => inputRef.current && inputRef.current.focus(), 0)
      : undefined;
    return () => clearTimeout(timeoutId);
  }, [menuControls.isOpen]);

  // Focus input on search change
  useEffect(() => {
    const timeoutId = setTimeout(
      () => inputRef.current && inputRef.current.focus(),
      0,
    );
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    if (!matchedFacility?.id) {
      // No selected facility, select the first available one from facilities list
      const nextSelectedFacilityId = creditFacilities[0]?.id;
      if (nextSelectedFacilityId) onFacilitySelect(nextSelectedFacilityId);
    }
  }, [creditFacilities, matchedFacility?.id, onFacilitySelect]);

  // Empty/loading state
  if (hideWhenNotEnoughFacilities && creditFacilities.length < 2) return null;

  return (
    <Flex flexDirection="column" flexGrow={1} maxWidth="100%" {...flexProps}>
      {matchedFacility && creditFacilities.length > 0 ? (
        <>
          <Box h="0px" alignSelf="flex-start" zIndex="docked">
            <Text
              className="select-label"
              style={{ transform: 'translate(16px,-8px)' }}
              bgColor="white"
              fontSize="xs"
              p="0 0.25rem"
              color="gray"
              borderRadius="md"
            >
              Credit Facility
            </Text>
          </Box>
          <Menu
            {...menuControls}
            autoSelect={false}
            placement={placement}
            matchWidth={matchWidth}
          >
            <MenuButton
              isDisabled={isDisabled}
              as={Button}
              rightIcon={<ChevronDownIcon />}
              fontWeight="normal"
              maxWidth="100%"
              textAlign="left"
              variant="outline"
              sx={{
                '> span': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '&:focus': {
                  boxShadow: 'none',
                },
              }}
              {...menuButtonProps}
            >
              {matchedFacility?.name}
            </MenuButton>
            <MenuList
              maxHeight="250px"
              maxWidth="100%"
              overflowY="auto"
              zIndex="dropdown"
              {...menuListProps}
            >
              {isSearchable && (
                <Flex p="0.5rem">
                  <SearchBarInput
                    ref={inputRef}
                    query={searchQuery}
                    onChangeQuery={(newQuery) => setSearchQuery(newQuery)}
                  />
                </Flex>
              )}
              {filteredFacilities.map((facility) => (
                <MenuItem
                  background={
                    matchedFacility?.id === facility.id
                      ? colors.gray[100]
                      : undefined
                  }
                  key={facility.id}
                  onClick={() => onFacilitySelect(facility.id)}
                >
                  {facility.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </>
      ) : (
        <Badge colorScheme="red" marginTop="0.75rem">
          No active facilities
        </Badge>
      )}
    </Flex>
  );
};

// Exports
type CreditFacilitySelectProps = {
  isDisabled?: boolean;
  creditFacilitySlugFilter?: (creditFacilitySlug: string) => boolean;
};
export const CreditFacilitySelect = ({
  isDisabled,
  creditFacilitySlugFilter,
}: CreditFacilitySelectProps) => {
  const { creditFacilityId, setCreditFacilityId } = useCreditFacilityContext();
  return (
    <CommonFacilitySelect
      isSearchable
      isDisabled={isDisabled}
      selectedFacilityId={creditFacilityId}
      facilitySlugFilter={creditFacilitySlugFilter}
      onFacilitySelect={(selectedCreditFacilityId: string) => {
        setCreditFacilityId(selectedCreditFacilityId);
      }}
    />
  );
};

type CreditFacilitySelectWithQueryParamsProps = {
  isDisabled?: boolean;
  creditFacilitySlugFilter?: (creditFacilitySlug: string) => boolean;
  onSelectRedirectLink: (creditFacilityId: string) => string;
};
export const CreditFacilitySelectWithQueryParams = ({
  isDisabled,
  creditFacilitySlugFilter,
  onSelectRedirectLink,
}: CreditFacilitySelectWithQueryParamsProps) => {
  const navigate = useNavigate();
  const query = useQueryParams();
  let queryParamCreditFacilityId = query.get(creditFacilityIdQueryParam);
  queryParamCreditFacilityId = queryParamCreditFacilityId
    ? queryParamCreditFacilityId.replace(/['"]/g, '')
    : queryParamCreditFacilityId;
  const [creditFacilityId, setCreditFacilityId] = useState(
    queryParamCreditFacilityId ?? undefined,
  );

  return (
    <CommonFacilitySelect
      isSearchable
      isDisabled={isDisabled}
      selectedFacilityId={creditFacilityId}
      facilitySlugFilter={creditFacilitySlugFilter}
      onFacilitySelect={(selectedCreditFacilityId: string) => {
        setCreditFacilityId(selectedCreditFacilityId);
        const redirectLink = onSelectRedirectLink(selectedCreditFacilityId);
        navigate(redirectLink);
      }}
    />
  );
};

type CreditFacilitySelectWithUrlParamsProps = {
  isDisabled?: boolean;
  creditFacilitySlugFilter?: (creditFacilitySlug: string) => boolean;
  onSelectRedirectLink: (creditFacilityId: string) => string;
};
export const CreditFacilitySelectWithUrlParams = ({
  isDisabled,
  creditFacilitySlugFilter,
  onSelectRedirectLink,
}: CreditFacilitySelectWithUrlParamsProps) => {
  // Custom
  const navigate = useNavigate();
  const params = useParams();
  const [creditFacilityId, setCreditFacilityId] = useState(
    RouteParamsStruct.parse(params).creditFacilityId,
  );

  return (
    <CommonFacilitySelect
      isSearchable
      isDisabled={isDisabled}
      selectedFacilityId={creditFacilityId}
      facilitySlugFilter={creditFacilitySlugFilter}
      onFacilitySelect={(selectedCreditFacilityId: string) => {
        setCreditFacilityId(selectedCreditFacilityId);
        const redirectLink = onSelectRedirectLink(selectedCreditFacilityId);
        navigate(redirectLink);
      }}
    />
  );
};
